import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { apiFetch } from '../api/client'
import type { CreateGlowRequest, Glow, UpdateGlowRequest } from '../api/types'

interface GlowListResponse {
  glows: Glow[]
  // Opaque cursor for the following page; "" / absent once the owner's set
  // is exhausted (API2 — GET /v1/glows is now cursor-paginated).
  next?: string
}

// Hard ceiling on auto-pagination so a pathological owner (or a server bug
// that never empties `next`) can't spin forever. 100 pages × the server's
// 100-default page size = 10k glows, far past any real account.
const maxGlowPages = 100

export function useGlows() {
  const { idTokenProvider, user } = useAuth()
  return useQuery({
    queryKey: ['glows'],
    // GET /v1/glows is cursor-paginated ({glows, next}); follow `next` and
    // concatenate every page so consumers (the list + the glows_used
    // derivation) see the owner's COMPLETE set, never a silently-truncated
    // first 100 (API2). Most owners fit in one page, so this is a single
    // request in the common case.
    queryFn: async ({ signal }) => {
      const all: Glow[] = []
      let cursor = ''
      for (let page = 0; page < maxGlowPages; page++) {
        const suffix = cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''
        const res = await apiFetch<GlowListResponse>(
          `/v1/glows${suffix}`,
          idTokenProvider,
          { signal },
        )
        all.push(...(res.glows ?? []))
        if (!res.next) {
          break
        }
        cursor = res.next
      }
      return all
    },
    enabled: !!user,
  })
}

export function useGlow(id: string) {
  const { idTokenProvider, user } = useAuth()
  return useQuery({
    queryKey: ['glow', id],
    queryFn: ({ signal }) => apiFetch<Glow>(`/v1/glows/${id}`, idTokenProvider, { signal }),
    enabled: !!user && !!id,
  })
}

export function useCreateGlow() {
  const { idTokenProvider } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateGlowRequest) =>
      apiFetch<Glow>('/v1/glows', idTokenProvider, { method: 'POST', body }),
    onSuccess: () => {
      // The list and any cached single-glow queries are now stale.
      qc.invalidateQueries({ queryKey: ['glows'] })
    },
  })
}

// useDeleteGlow soft-deletes a glow (DELETE /v1/glows/{id} → 204). The
// server stops serving it immediately and frees a glow slot; the assets +
// data are retained for 30 days, then purged, and the name stays reserved
// until then (CLAUDE.md §3 last_purged_at semantics). On success we
// invalidate ['glows'] so the list + freed-slot count refresh, then
// navigate back to the list. A `not_found` (already deleted / race) is the
// caller's to handle — it also lands the user on the list.
export function useDeleteGlow(id: string) {
  const { idTokenProvider } = useAuth()
  const qc = useQueryClient()
  const navigate = useNavigate()
  return useMutation({
    mutationFn: () =>
      apiFetch<void>(`/v1/glows/${id}`, idTokenProvider, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['glows'] })
      qc.removeQueries({ queryKey: ['glow', id] })
      navigate('/glows')
    },
  })
}

export function useUpdateGlow(id: string) {
  const { idTokenProvider } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: UpdateGlowRequest) =>
      apiFetch<Glow>(`/v1/glows/${id}`, idTokenProvider, {
        method: 'PATCH',
        body,
      }),
    onSuccess: (data) => {
      qc.setQueryData(['glow', id], data)
      qc.invalidateQueries({ queryKey: ['glows'] })
    },
  })
}
